import { PLAN_DETAILS, PLAN_IDS, formatPrice, type CatalogPlan, type PlanId } from './Catalog';

/** What validate-coupon-code answers for a code it found (MUS-629). Amounts are in cents. */
export interface CouponValidation {
  percentOff: number | null;
  amountOff: number | null;
  currency: string | null;
  duration: 'once' | 'repeating' | 'forever';
  durationInMonths: number | null;
  /** The plans the code takes money off; null when the server could not say (or is older). */
  appliesToPlans: PlanId[] | null;
  firstTimeOnly: boolean;
  minimumAmountCents: number | null;
  minimumAmountCurrency: string | null;
}

export interface PlanDiscount {
  amountCents: number;
  /** How long the discounted price lasts, in the plan's own billing. */
  duration: string;
}

const DURATIONS = ['once', 'repeating', 'forever'] as const;

const isPlanId = (value: unknown): value is PlanId => PLAN_IDS.some((id) => id === value);
const numberOrNull = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;
const stringOrNull = (value: unknown) => (typeof value === 'string' && value ? value : null);
const sameCurrency = (first: string, second: string) =>
  first.toLowerCase() === second.toLowerCase();

/**
 * The plans an answer names. A list naming only plans this page does not know is read as unknown,
 * not as "none of these plans", so a good code is never withheld from checkout over a new plan id.
 */
function plansNamed(value: unknown): PlanId[] | null {
  if (!Array.isArray(value)) return null;
  const known = value.filter(isPlanId);
  return known.length === 0 && value.length > 0 ? null : known;
}

/** The validate answer, or null when it does not describe a discount this page can show. */
export function parseCouponValidation(value: unknown): CouponValidation | null {
  if (!value || typeof value !== 'object') return null;
  const answer = value as Record<string, unknown>;
  const duration = DURATIONS.find((known) => known === answer.duration);
  const percentOff = numberOrNull(answer.percentOff);
  const amountOff = numberOrNull(answer.amountOff);
  const durationInMonths = numberOrNull(answer.durationInMonths);
  const minimumAmountCents = numberOrNull(answer.minimumAmountCents);
  const validPercent = percentOff !== null && percentOff > 0 && percentOff <= 100;
  const validAmount = amountOff !== null && amountOff > 0 && !!stringOrNull(answer.currency);
  if (
    !duration ||
    validPercent === validAmount ||
    (duration === 'repeating' && !(durationInMonths && durationInMonths > 0))
  )
    return null;
  return {
    percentOff: validPercent ? percentOff : null,
    amountOff: validAmount ? amountOff : null,
    currency: stringOrNull(answer.currency),
    duration,
    durationInMonths,
    appliesToPlans: plansNamed(answer.appliesToPlans),
    firstTimeOnly: answer.firstTimeOnly === true,
    minimumAmountCents: minimumAmountCents && minimumAmountCents > 0 ? minimumAmountCents : null,
    minimumAmountCurrency: stringOrNull(answer.minimumAmountCurrency),
  };
}

const monthsInWords = (months: number) =>
  months % 12 === 0
    ? months === 12
      ? 'year'
      : `${months / 12} years`
    : months === 1
      ? 'month'
      : `${months} months`;

/**
 * A repeating code discounts every invoice raised in its first N months, so a plan billed every L
 * months gets ceil(N / L) discounted payments, which cover that many of its billing periods.
 */
function planDurationInWords(coupon: CouponValidation, planMonths: number) {
  if (coupon.duration === 'once') return 'for your first payment';
  // Short enough for one line on the narrowest plan card, where a second would move SELECT.
  if (coupon.duration === 'forever') return 'while you stay subscribed';
  const discountedPayments = Math.ceil((coupon.durationInMonths ?? 1) / planMonths);
  return `for the first ${monthsInWords(discountedPayments * planMonths)}`;
}

function couponDurationInWords(coupon: CouponValidation) {
  if (coupon.duration === 'once') return 'your first payment';
  if (coupon.duration === 'forever') return 'as long as you stay subscribed';
  return `for the first ${monthsInWords(coupon.durationInMonths ?? 1)}`;
}

/**
 * A percentage off leaves a whole number of cents. Stripe rounds the discount, not the price, and
 * rounds a half cent of discount up: 50% off $19.97 takes $9.99 off and charges $9.98 (measured in
 * Stripe test mode for all three plans). An amount off is only taken from a price in the code's own
 * currency.
 */
function discountedAmountCents(
  amountCents: number,
  coupon: CouponValidation,
  catalogCurrency: string,
) {
  if (coupon.percentOff !== null) {
    // In whole hundredths of a percent, so a half cent is found exactly (no float product).
    const hundredths = Math.round(coupon.percentOff * 100);
    return amountCents - Math.floor((amountCents * hundredths + 5000) / 10000);
  }
  if (
    coupon.amountOff !== null &&
    coupon.currency &&
    sameCurrency(coupon.currency, catalogCurrency)
  )
    return Math.max(0, amountCents - coupon.amountOff);
  return null;
}

function meetsMinimum(plan: CatalogPlan, coupon: CouponValidation, catalogCurrency: string) {
  if (coupon.minimumAmountCents === null) return true;
  if (coupon.minimumAmountCurrency && !sameCurrency(coupon.minimumAmountCurrency, catalogCurrency))
    return false;
  return plan.amountCents >= coupon.minimumAmountCents;
}

/**
 * The price a plan shows with this code, or null when the page cannot say it is discounted. A
 * first-time-only code is refused for anyone with an earlier transaction, a trial included, and
 * most people on this page have one, so no plan shows its price.
 */
export function planDiscount(
  plan: CatalogPlan,
  catalogCurrency: string,
  coupon: CouponValidation,
): PlanDiscount | null {
  if (
    coupon.firstTimeOnly ||
    !coupon.appliesToPlans?.includes(plan.id) ||
    !meetsMinimum(plan, coupon, catalogCurrency)
  )
    return null;
  const amountCents = discountedAmountCents(plan.amountCents, coupon, catalogCurrency);
  if (amountCents === null) return null;
  return { amountCents, duration: planDurationInWords(coupon, PLAN_DETAILS[plan.id].months) };
}

/** True when the server says the code takes nothing off any plan on this page. */
export const couponCoversNoPlan = (coupon: CouponValidation) =>
  Array.isArray(coupon.appliesToPlans) && coupon.appliesToPlans.length === 0;

const planTitlesInWords = (plans: PlanId[]) =>
  PLAN_IDS.filter((id) => plans.includes(id))
    .map((id) => PLAN_DETAILS[id].title)
    .join(' and ');

/** The line under the coupon field once a code is accepted. */
export function couponAcceptedLine(coupon: CouponValidation) {
  const discount =
    coupon.percentOff !== null
      ? `${coupon.percentOff}%`
      : formatPrice((coupon.amountOff ?? 0) / 100, (coupon.currency ?? 'usd').toUpperCase());
  const forSomePlans =
    coupon.appliesToPlans && coupon.appliesToPlans.length < PLAN_IDS.length
      ? ` on the ${planTitlesInWords(coupon.appliesToPlans)}`
      : '';
  const minimum =
    coupon.minimumAmountCents !== null
      ? `on purchases of at least ${formatPrice(
          coupon.minimumAmountCents / 100,
          (coupon.minimumAmountCurrency ?? coupon.currency ?? 'usd').toUpperCase(),
        )}`
      : '';
  const terms = [
    `${discount} off ${couponDurationInWords(coupon)}${forSomePlans}`,
    coupon.firstTimeOnly ? 'for first-time subscribers' : '',
    minimum,
  ]
    .filter(Boolean)
    .join(', ');
  if (coupon.firstTimeOnly)
    return `Coupon accepted: ${terms}. Whether it applies to your account is confirmed at checkout.`;
  return coupon.appliesToPlans
    ? `Coupon accepted: ${terms}. Your final price is confirmed at checkout.`
    : `Coupon accepted: ${terms}, applied at checkout.`;
}
